/**
 * DemoBanner — Inspector demo banner surface (v0.12.2).
 *
 * Renders a sticky yellow ribbon at the top of every page when the
 * server reports `visible: true` for `inspector.demo_banner` in
 * tenant_config. The banner reads the endpoint:
 *   GET /api/v1/admin/inspector-banner
 * and writes back via:
 *   POST /api/v1/admin/inspector-banner   (dismiss action)
 *
 * The endpoints persist to tenant_config.entries so dismissal survives
 * mock-api restarts and propagates across tabs / users.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import styles from './DemoBanner.module.css';

interface DemoBannerPayload {
  visible: boolean;
  message: string | null;
  dismissed_at: string | null;
  actor_user_id?: string | null;
  updated_at_utc?: string;
}

const DEFAULT_MESSAGE =
  'Demo / inspection mode — data may include scenario-loaded rows. Every action is audited and reversible.';

export function DemoBanner() {
  const qc = useQueryClient();
  const q = useQuery<DemoBannerPayload>({
    queryKey: ['admin', 'inspector-banner'],
    queryFn: () => apiFetch<DemoBannerPayload>('/api/v1/admin/inspector-banner'),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const dismiss = useMutation({
    mutationFn: () =>
      apiFetch<DemoBannerPayload>('/api/v1/admin/inspector-banner', {
        method: 'POST',
        body: {
          visible: false,
          message: q.data?.message ?? null,
          dismissed_at: new Date().toISOString(),
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'inspector-banner'] }),
  });

  if (!q.data?.visible) return null;
  const msg = q.data.message?.trim() || DEFAULT_MESSAGE;

  return (
    <div className={styles.banner} role="status" aria-live="polite">
      <span className={styles.icon} aria-hidden>
        ⚠️
      </span>
      <span className={styles.message}>{msg}</span>
      <button
        type="button"
        className={styles.dismiss}
        onClick={() => dismiss.mutate()}
        disabled={dismiss.isPending}
        aria-label="Dismiss demo banner"
      >
        Dismiss
      </button>
    </div>
  );
}
